import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FeedbackDialogService } from '../../services/feedback-dialog.service';
import { AppFeedbackService } from '../../services/app-feedback.service';
import { ToastFeedbackService } from '../../services/toast-feedback.service';
import { ThemeService } from '../../services/theme.service';
import { AppVersionService } from '../../services/app-version.service';
import { AuthService } from '../../services/auth.service';
import {
  APP_FEEDBACK_CATEGORIES,
  APP_FEEDBACK_DETAILS_MAX,
  APP_FEEDBACK_DETAILS_MIN,
  type AppFeedbackCategory,
  validateAppFeedbackDetails,
} from '../../models/app-feedback.model';
import { formatUserFacingError } from '../../utils/user-facing-error.util';
import { UserActivityService } from '../../services/user-activity.service';

/** PAT-354 / PAT-366 / PAT-367 — Send feedback dialog (app-level; not message like/dislike). */
@Component({
  selector: 'app-feedback-dialog',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './feedback-dialog.component.html',
  styleUrl: './feedback-dialog.component.scss',
})
export class FeedbackDialogComponent {
  readonly dialog = inject(FeedbackDialogService);
  private readonly api = inject(AppFeedbackService);
  private readonly toast = inject(ToastFeedbackService);
  private readonly theme = inject(ThemeService);
  private readonly versionSvc = inject(AppVersionService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly userActivity = inject(UserActivityService);

  readonly categories = APP_FEEDBACK_CATEGORIES;
  readonly detailsMin = APP_FEEDBACK_DETAILS_MIN;
  readonly detailsMax = APP_FEEDBACK_DETAILS_MAX;

  readonly category = signal<AppFeedbackCategory | null>(null);
  readonly rating = signal<number | null>(null);
  readonly details = signal('');
  readonly detailsError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly success = signal(false);

  readonly detailsCount = computed(() => this.details().length);
  readonly canSubmit = computed(() => {
    if (this.submitting() || this.success()) return false;
    if (!this.category()) return false;
    return validateAppFeedbackDetails(this.details()) === null;
  });

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.dialog.active() || this.submitting()) return;
    this.close();
  }

  onBackdropClick(): void {
    if (this.submitting()) return;
    this.close();
  }

  close(): void {
    this.resetForm();
    this.dialog.close();
  }

  onCategoryChange(value: AppFeedbackCategory): void {
    this.category.set(value);
    this.submitError.set(null);
  }

  onRatingChange(value: number | null): void {
    this.rating.set(value);
  }

  onDetailsChange(value: string): void {
    this.details.set(value);
    this.detailsError.set(validateAppFeedbackDetails(value));
    this.submitError.set(null);
  }

  async onSubmit(): Promise<void> {
    if (!this.auth.isLoggedIn()) {
      this.submitError.set('Sign in to send feedback.');
      return;
    }
    const category = this.category();
    if (!category) {
      this.submitError.set('Select a category.');
      return;
    }
    const detailsErr = validateAppFeedbackDetails(this.details());
    if (detailsErr) {
      this.detailsError.set(detailsErr);
      return;
    }
    if (this.submitting()) return;

    this.submitting.set(true);
    this.submitError.set(null);
    try {
      // PAT-366 — technical context is automatic for Report a Bug only (no UI toggle).
      const includeTech = category === 'bug';
      await this.api.submitFeedback({
        category,
        details: this.details(),
        rating: this.rating(),
        includeTechnicalContext: includeTech,
        technicalContext: includeTech ? this.buildTechnicalContext() : null,
      });
      this.success.set(true);
      this.userActivity.trackAction('app_feedback_submitted');
      await this.toast.show('Thanks — your feedback was sent.', { variant: 'success' });
      // Brief success state then close to idle.
      window.setTimeout(() => this.close(), 400);
    } catch (err) {
      this.submitError.set(
        formatUserFacingError(err, 'Could not send feedback. Please try again.'),
      );
    } finally {
      this.submitting.set(false);
    }
  }

  private buildTechnicalContext(): {
    route: string;
    theme: string;
    appVersion: string;
    browserFamily: string;
  } {
    const route = (this.router.url || '/').split('?')[0] || '/';
    const theme = this.theme.resolvedTheme();
    const appVersion = this.versionSvc.serverVersion().trim() || 'unknown';
    return {
      route,
      theme,
      appVersion,
      browserFamily: detectBrowserFamily(),
    };
  }

  private resetForm(): void {
    this.category.set(null);
    this.rating.set(null);
    this.details.set('');
    this.detailsError.set(null);
    this.submitError.set(null);
    this.submitting.set(false);
    this.success.set(false);
  }
}

function detectBrowserFamily(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Other';
}
